import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { AlertCircle, CheckCircle, Eye, EyeOff, ArrowLeft } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PasswordReset = () => {
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    // Récupérer le token depuis l'URL
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token');
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    } else {
      setIsError(true);
      setMessage("Token de réinitialisation manquant. Veuillez utiliser le lien reçu par email.");
    }
  }, []);

  const validatePassword = (password) => {
    if (password.length < 6) {
      return "Le mot de passe doit contenir au moins 6 caractères";
    }
    return null;
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    // Validations
    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setIsError(true);
      setMessage(passwordError);
      return;
    }

    if (newPassword !== confirmPassword) {
      setIsError(true);
      setMessage("Les mots de passe ne correspondent pas");
      return;
    }

    setIsLoading(true);
    setMessage("");
    setIsError(false);
    
    try {
      const response = await axios.post(`${API}/auth/reset-password/confirm`, {
        token: token,
        new_password: newPassword
      });
      
      setIsSuccess(true);
      setMessage(response.data.message);
      setNewPassword("");
      setConfirmPassword("");
      
      // Rediriger vers la page de connexion après 3 secondes
      setTimeout(() => {
        window.location.href = "/";
      }, 3000);
      
    } catch (error) {
      setIsError(true);
      const errorMessage = error.response?.data?.detail || "Erreur lors de la réinitialisation";
      setMessage(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const goHome = () => {
    window.location.href = "/";
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      {/* Background Animation */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 opacity-20">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-green-400 rounded-full animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 3}s`
              }}
            />
          ))}
        </div>
      </div>

      <Card className="w-full max-w-md bg-black/80 border-green-800/30 backdrop-blur-md relative z-10">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-green-600 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl font-bold text-black">e</span>
          </div>
          <CardTitle className="text-2xl text-green-400">
            🔐 Nouveau mot de passe
          </CardTitle>
          <p className="text-green-300 text-sm">
            Définissez votre nouveau mot de passe sécurisé
          </p>
        </CardHeader>
        
        <CardContent>
          {!token && !isSuccess ? (
            // Token manquant
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center space-x-2 text-red-400">
                <AlertCircle className="w-5 h-5" />
                <span>Token de réinitialisation invalide</span>
              </div>
              <p className="text-gray-400 text-sm">
                Veuillez utiliser le lien reçu par email pour réinitialiser votre mot de passe.
              </p>
              <Button onClick={goHome} className="bg-green-600 hover:bg-green-500 text-black">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour à l'accueil
              </Button>
            </div>
          ) : isSuccess ? (
            // Succès
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center space-x-2 text-green-400">
                <CheckCircle className="w-6 h-6" />
                <span className="font-semibold">Mot de passe modifié !</span>
              </div>
              <p className="text-green-300">{message}</p>
              <div className="bg-green-950/20 border border-green-800/30 rounded-lg p-4">
                <p className="text-green-400 text-sm">
                  ✨ Redirection automatique dans 3 secondes...
                </p>
                <p className="text-gray-400 text-xs mt-2">
                  Vous pouvez maintenant vous connecter avec votre nouveau mot de passe
                </p>
              </div>
              <Button onClick={goHome} className="bg-green-600 hover:bg-green-500 text-black">
                Se connecter maintenant
              </Button>
            </div>
          ) : (
            // Formulaire de réinitialisation
            <form onSubmit={handleResetPassword} className="space-y-6">
              {/* Message d'erreur */}
              {isError && message && (
                <div className="bg-red-950/20 border border-red-800/30 rounded-lg p-3">
                  <div className="flex items-center space-x-2 text-red-400">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm">{message}</span>
                  </div>
                </div>
              )}

              {/* Nouveau mot de passe */}
              <div className="space-y-2">
                <label className="text-green-300 text-sm">Nouveau mot de passe</label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Minimum 6 caractères"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="bg-black/60 border-green-700/50 text-green-100 pr-10"
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 text-green-400 hover:text-green-300"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {/* Confirmation mot de passe */}
              <div className="space-y-2">
                <label className="text-green-300 text-sm">Confirmer le mot de passe</label>
                <div className="relative">
                  <Input
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Répétez le mot de passe"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="bg-black/60 border-green-700/50 text-green-100 pr-10"
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 text-green-400 hover:text-green-300"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {/* Conseils sécurité */}
              <div className="bg-blue-950/20 border border-blue-800/30 rounded-lg p-3">
                <h4 className="text-blue-400 text-sm font-medium mb-2">💡 Conseils sécurité :</h4>
                <ul className="text-blue-300 text-xs space-y-1">
                  <li>• Au moins 6 caractères</li>
                  <li>• Mélangez lettres, chiffres et symboles</li>
                  <li>• Évitez les mots courants</li>
                  <li>• N'utilisez pas d'informations personnelles</li>
                </ul>
              </div>

              {/* Bouton de soumission */}
              <Button
                type="submit"
                disabled={isLoading || !newPassword || !confirmPassword}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 text-black font-semibold h-12"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin mr-2" />
                    Mise à jour...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Modifier le mot de passe
                  </>
                )}
              </Button>

              {/* Retour */}
              <Button
                type="button"
                variant="outline"
                onClick={goHome}
                className="w-full border-green-700 text-green-300 hover:bg-green-950"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour à l'accueil
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};